import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const reveal = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

export default function Signup() {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [reservation, setReservation] = useState(null);

  const validate = () => {
    const next = {};
    if (!EMAIL_RE.test(email.trim())) next.email = "Enter a valid email address.";
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7 || digits.length > 15) next.phone = "Enter a valid phone number.";
    if (!consent) next.consent = "Consent is required to reserve your discount.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (status === "submitting" || !validate()) return;
    setStatus("submitting");
    try {
      const { data } = await axios.post(`${API}/signups`, {
        email: email.trim(),
        phone: phone.trim(),
        consent,
      });
      setReservation(data);
      setStatus("success");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        setErrors({ email: "This email is already reserved for Batch 01." });
      } else if (Array.isArray(detail)) {
        const next = {};
        detail.forEach((d) => {
          const field = d.loc?.[d.loc.length - 1];
          if (field === "email") next.email = "Enter a valid email address.";
          if (field === "phone") next.phone = "Enter a valid phone number.";
          if (field === "consent") next.consent = "Consent is required to reserve your discount.";
        });
        setErrors(next);
      } else {
        setErrors({ form: "Something went wrong. Please try again." });
      }
      setStatus("idle");
    }
  };

  const inputClass = (hasError) =>
    `w-full border bg-transparent px-5 py-4 text-base font-light text-white placeholder:text-neutral-600 outline-none transition-colors duration-300 focus:border-white/60 ${
      hasError ? "border-white/60" : "border-white/15"
    }`;

  return (
    <section id="access" data-testid="signup-section" className="border-t border-white/10 bg-[#0A0A0A]">
      <div className="mx-auto flex max-w-7xl justify-center px-6 py-14 sm:px-12 sm:py-20 lg:px-16 lg:py-24">
        <motion.div {...reveal} transition={{ duration: 1.1, delay: 0.2, ease: EASE }} className="relative w-full max-w-xl border border-white/10 bg-[#0D0D0D] p-7 sm:p-10" data-testid="signup-card">
          <span className="absolute -top-px left-8 h-px w-16 bg-white/60" />
          <AnimatePresence mode="wait">
            {status === "success" && reservation ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE }}
                className="flex min-h-[420px] flex-col items-start justify-center"
                data-testid="signup-success-message"
              >
                <img src="/logo-mark.png" alt="" className="mb-8 h-12 w-auto opacity-90" />
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-500">Reservation Confirmed</p>
                <p className="mt-5 font-serif text-3xl font-light tracking-tight text-white sm:text-4xl">
                  Your 30% is locked in.
                </p>
                <p className="mt-6 font-mono text-sm tracking-[0.2em] text-neutral-300" data-testid="signup-reservation-code">
                  {reservation.reservation_code}
                </p>
                <p className="mt-6 max-w-sm text-sm font-light leading-relaxed text-neutral-400">
                  We will notify <span className="text-white">{reservation.email}</span> and your phone the moment
                  Batch 01 opens in November 2026.
                  {reservation.email_sent && (
                    <span className="mt-3 block text-neutral-500" data-testid="signup-email-sent-note">
                      A confirmation email is on its way to your inbox.
                    </span>
                  )}
                </p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5 }}
                onSubmit={submit}
                noValidate
                data-testid="signup-form"
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-neutral-500">Early Access Reservation</p>

                <div className="mt-8 space-y-6">
                  <div>
                    <label htmlFor="signup-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                      Email Address
                    </label>
                    <input
                      id="signup-email"
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: undefined, form: undefined })); }}
                      placeholder="you@laboratory.com"
                      className={inputClass(errors.email)}
                      data-testid="signup-email-input"
                      autoComplete="email"
                    />
                    {errors.email && <p className="mt-2 font-mono text-[11px] tracking-wide text-neutral-300" data-testid="signup-email-error">! {errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="signup-phone" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                      Phone Number
                    </label>
                    <input
                      id="signup-phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => { setPhone(e.target.value); setErrors((p) => ({ ...p, phone: undefined, form: undefined })); }}
                      placeholder="+1 (555) 000-0000"
                      className={inputClass(errors.phone)}
                      data-testid="signup-phone-input"
                      autoComplete="tel"
                    />
                    {errors.phone && <p className="mt-2 font-mono text-[11px] tracking-wide text-neutral-300" data-testid="signup-phone-error">! {errors.phone}</p>}
                  </div>

                  <div>
                    <label className="flex cursor-pointer items-start gap-4" data-testid="signup-consent-label">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => { setConsent(e.target.checked); setErrors((p) => ({ ...p, consent: undefined, form: undefined })); }}
                        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer appearance-none border border-white/30 bg-transparent transition-colors duration-200 checked:border-white checked:bg-white"
                        data-testid="signup-consent-checkbox"
                      />
                      <span className="text-xs font-light leading-relaxed text-neutral-400">
                        I consent to receive SMS and email notifications from AxenLabs
                        regarding Batch 01 availability and my 30% early access discount.
                      </span>
                    </label>
                    {errors.consent && <p className="mt-2 font-mono text-[11px] tracking-wide text-neutral-300" data-testid="signup-consent-error">! {errors.consent}</p>}
                  </div>
                </div>

                {errors.form && <p className="mt-6 font-mono text-[11px] tracking-wide text-neutral-300" data-testid="signup-form-error">! {errors.form}</p>}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  data-testid="signup-submit-button"
                  className="mt-10 w-full rounded-full bg-white px-8 py-4 font-mono text-xs font-medium uppercase tracking-[0.2em] text-black transition-all duration-300 hover:-translate-y-0.5 hover:bg-neutral-200 disabled:cursor-wait disabled:opacity-60"
                >
                  {status === "submitting" ? "Reserving…" : "Unlock My 30% Discount"}
                </button>

                <p className="mt-6 text-[11px] font-light leading-relaxed text-neutral-500" data-testid="signup-privacy-note">
                  By reserving, you agree to receive SMS &amp; email notifications from
                  AxenLabs regarding Batch 01 availability. Unsubscribe anytime.
                  Standard message and data rates may apply. We never share your information.
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
